import { PlayerComparisonKey } from "@suns/api";
import {
  cn,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Text,
} from "@suns/design-system";
import { Link as RouterLink } from "react-router-dom";

interface Player {
  playerId: number;
  fullName: string;
}

interface PlayerComparison {
  closest: Player;
  secondClosest: Player;
  thirdClosest: Player;
}

interface PlayerComparisonsProps {
  comparisons: Record<PlayerComparisonKey, PlayerComparison | null>;
  print?: boolean;
}

const rows = [
  {
    key: "size",
    label: "Size",
  },
  {
    key: "perimeter",
    label: "Perimeter",
  },
  {
    key: "lateral",
    label: "Lateral",
  },
  {
    key: "speed",
    label: "Speed",
  },
  {
    key: "vertical",
    label: "Vertical",
  },
  {
    key: "all",
    label: "All",
  },
] satisfies {
  key: PlayerComparisonKey;
  label: string;
}[];

export function PlayerComparisons({
  comparisons,
  print = false,
}: PlayerComparisonsProps) {
  return (
    <Table
      className={cn(
        "border",
        print && [
          "[&_td]:p-1",
          "[&_td]:text-2xs",
          "[&_th]:p-1",
          "[&_th]:text-2xs",
        ]
      )}
    >
      <TableHeader>
        <TableRow>
          <TableHead>Category</TableHead>
          <TableHead>Closest</TableHead>
          <TableHead>Second</TableHead>
          <TableHead>Third</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map(({ key, label }) => {
          const comparison = comparisons[key];

          if (
            !comparison?.closest &&
            !comparison?.secondClosest &&
            !comparison?.thirdClosest
          ) {
            return null;
          }

          return (
            <TableRow key={`${key}-comparison`}>
              <TableCell>{label}</TableCell>
              <PlayerCell player={comparison?.closest} link={!print} />
              <PlayerCell player={comparison?.secondClosest} link={!print} />
              <PlayerCell player={comparison?.thirdClosest} link={!print} />
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

function PlayerCell({
  player,
  link = true,
}: {
  player?: Player;
  link?: boolean;
}) {
  if (!player) {
    return <TableCell>--</TableCell>;
  }

  return (
    <TableCell>
      {link ? (
        <Link asChild>
          <RouterLink to={playerUrl(player.playerId)}>
            {player.fullName}
          </RouterLink>
        </Link>
      ) : (
        <Text size="2xs">{player.fullName}</Text>
      )}
    </TableCell>
  );
}

function playerUrl(playerId: number) {
  return `/players/${playerId}`;
}
